import { useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";
import {
  ChevronLeft,
  ChevronRight,
  Loader,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router";
import t from "@/lang/lang";
import {
  useGfsCatalogGetQuery,
  useGfsCatalogItemDeleteImageMutation,
  useGfsCatalogItemDeleteMutation,
  useGfsCatalogItemImageMutation,
  useGfsCatalogItemRefreshMutation,
  useGfsCatalogItemRefreshPreviewQuery,
  useGfsCatalogItemShowImageQuery,
  useGfsCatalogItemUpdateMutation,
} from "@/services/backend/gfs/catalog/api";
import {
  GfsCatalogItem,
  GfsConsumable,
  GfsMonitorConsumable,
  GfsMonitorSnapshot,
} from "@/services/backend/gfs/catalog/types";
import {
  usePestcontrolPestGroupListQuery,
  usePestcontrolPestListQuery,
} from "@/services/backend/pestcontrol/api/pest-api";
import {
  Pest,
  PestGroup,
  PestGroupSnapshot,
  PestKind,
  PestSnapshot,
} from "@/services/backend/pestcontrol/types/pest";
import {
  comparePests,
  formatMonitorType,
  groupToSnapshot,
  groupsFromPests,
  mergeGroups,
  pestToSnapshot,
} from "@/services/backend/pestcontrol/lib/monitor";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { TimeNumberInput } from "@/shared/components/inputs/time-number-input";
import { ImageUpload } from "@/shared/components/images/image-upload";
import { PestcontrolField } from "@/services/backend/pestcontrol/components/pestcontrol-field";
import { FloatNumberInput } from "@/shared/components/domain/qpm/inputs/number-input";

export function GfsCatalogMonitorEditor({
  catalogId,
  itemId,
}: {
  catalogId: string;
  itemId: string;
}) {
  const navigate = useNavigate();
  const catalogPath = `/base-data/processes/gfs/catalog/${catalogId}`;
  const { companyId } = useActiveCompany();
  const { data, error, isLoading } = useGfsCatalogGetQuery({ id: catalogId });
  const item = data?.items.find((candidate) => candidate.id === itemId);
  const [draft, setDraft] = useState<GfsCatalogItem | null>(null);
  const [updateItem, updateState] = useGfsCatalogItemUpdateMutation();
  const [deleteItem, deleteState] = useGfsCatalogItemDeleteMutation();
  const [refreshItem, refreshState] = useGfsCatalogItemRefreshMutation();
  const { data: refreshPreview } = useGfsCatalogItemRefreshPreviewQuery(
    { catalogId, itemId },
    { skip: !item?.sourceMonitorId },
  );
  const { data: pests } = usePestcontrolPestListQuery({
    company: { active: true, values: [companyId] },
    limit: 500,
    offset: 0,
  });
  const { data: groups } = usePestcontrolPestGroupListQuery({
    company: { active: true, values: [companyId] },
    limit: 500,
    offset: 0,
  });

  useEffect(() => {
    if (item) {
      setDraft(item);
    }
  }, [item]);

  const lockedGroups = useMemo(
    () => groupsFromPests(draft?.pests ?? []),
    [draft?.pests],
  );
  const lockedGroupIds = useMemo(
    () => new Set(lockedGroups.map((group) => group.sourceId)),
    [lockedGroups],
  );
  const sortedItemIds = useMemo(
    () =>
      [...(data?.items ?? [])]
        .sort((left, right) =>
          left.monitor.name.localeCompare(right.monitor.name),
        )
        .map((catalogItem) => catalogItem.id),
    [data?.items],
  );
  const currentIndex = sortedItemIds.indexOf(itemId);

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading || !data || !draft) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 p-4 text-sm">
        <Loader className="size-4 animate-spin" />
        {t("Lade Monitor ...")}
      </div>
    );
  }

  const save = () =>
    updateItem({
      catalogId,
      item: draft,
      installationTime: draft.installationTime,
      serviceTime: draft.serviceTime,
    });

  const remove = async () => {
    await deleteItem({ catalogId, itemId }).unwrap();
    navigate(catalogPath, { replace: true });
  };

  const applyRefresh = async () => {
    const response = await refreshItem({ catalogId, itemId }).unwrap();
    const refreshed = response.items.find(
      (candidate) => candidate.id === itemId,
    );
    if (refreshed) {
      setDraft(refreshed);
    }
  };

  const goPrev = () => {
    if (currentIndex <= 0) return;
    navigate(`../${sortedItemIds[currentIndex - 1]}`, { replace: true });
  };

  const goNext = () => {
    if (currentIndex < 0 || currentIndex >= sortedItemIds.length - 1) return;
    navigate(`../${sortedItemIds[currentIndex + 1]}`, { replace: true });
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold">
          {formatMonitorType(draft.monitor.type)} {t("Details")}
        </h3>
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goPrev}
                  disabled={currentIndex <= 0}
                >
                  <ChevronLeft className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("Vorheriger")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goNext}
                  disabled={
                    currentIndex < 0 || currentIndex >= sortedItemIds.length - 1
                  }
                >
                  <ChevronRight className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("Nächster")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            variant="outline"
            onClick={applyRefresh}
            disabled={refreshState.isLoading || !draft.sourceMonitorId}
          >
            <RefreshCw className="size-4" />
            {t("Aktualisieren")}
          </Button>
        </div>
      </div>
      {refreshPreview &&
        hasMonitorSourceChanges(
          refreshPreview.current.monitor,
          refreshPreview.preview.monitor,
        ) && (
          <div className="rounded-md border p-3 text-sm">
            <div className="font-medium">{t("Aktualisierung verfügbar")}</div>
            <div className="text-muted-foreground">
              {t("Die Vorschau zeigt, dass der Basismonitor geändert wurde.")}
            </div>
          </div>
        )}
      {refreshState.error && <RTKQueryErrorAlert error={refreshState.error} />}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="rounded-md border p-4">
            <h3 className="mb-4 text-base font-semibold">{t("GfS-Daten")}</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <PestcontrolField label={t("Preis")}>
                <FloatNumberInput
                  value={draft.price}
                  onChange={(price) => setDraft({ ...draft, price })}
                />
              </PestcontrolField>
              <PestcontrolField label={t("Installationszeit")}>
                <TimeNumberInput
                  value={draft.installationTime}
                  onChange={(installationTime) =>
                    setDraft({ ...draft, installationTime })
                  }
                />
              </PestcontrolField>
              <PestcontrolField label={t("Servicezeit")}>
                <TimeNumberInput
                  value={draft.serviceTime}
                  onChange={(serviceTime) =>
                    setDraft({ ...draft, serviceTime })
                  }
                />
              </PestcontrolField>
            </div>
          </div>
          <div className="rounded-md border p-4">
            <h3 className="mb-4 text-base font-semibold">{t("Schädlinge")}</h3>
            <PestAssignment
              pests={pests?.items ?? []}
              selected={draft.pests}
              onChange={(selected) =>
                setDraft({
                  ...draft,
                  pests: selected,
                  groups: mergeGroups(draft.groups, groupsFromPests(selected)),
                })
              }
            />
          </div>
          <div className="rounded-md border p-4">
            <h3 className="mb-4 text-base font-semibold">
              {t("Verbrauchsmaterialien")}
            </h3>
            <ConsumablesTable
              consumables={draft.consumables}
              catalogConsumables={data?.consumables ?? []}
              onChange={(consumables) => setDraft({ ...draft, consumables })}
            />
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-md border p-4">
            <h3 className="mb-4 text-base font-semibold">{t("Bild")}</h3>
            <CatalogItemImage catalogId={catalogId} item={draft} />
          </div>
          <div className="rounded-md border p-4">
            <h3 className="mb-4 text-base font-semibold">
              {t("Beschreibung")}
            </h3>
            <PestcontrolField label={t("Beschreibung")}>
              <Textarea
                value={draft.description}
                onChange={(event) =>
                  setDraft({ ...draft, description: event.target.value })
                }
              />
            </PestcontrolField>
          </div>
          <div className="rounded-md border p-4">
            <h3 className="mb-4 text-base font-semibold">
              {t("Schädlingsgruppen")}
            </h3>
            <GroupSelection
              groups={groups?.items ?? []}
              selected={draft.groups}
              lockedGroups={lockedGroups}
              lockedGroupIds={lockedGroupIds}
              onChange={(selected) => setDraft({ ...draft, groups: selected })}
            />
          </div>
        </div>
      </div>
      {updateState.error && <RTKQueryErrorAlert error={updateState.error} />}
      {deleteState.error && <RTKQueryErrorAlert error={deleteState.error} />}
      <div className="flex justify-between gap-2">
        <Button
          type="button"
          variant="destructive"
          onClick={remove}
          disabled={deleteState.isLoading}
        >
          <Trash2 className="size-4" />
          {t("Aus Katalog entfernen")}
        </Button>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(catalogPath)}
          >
            {t("Abbrechen")}
          </Button>
          <Button type="button" onClick={save} disabled={updateState.isLoading}>
            {t("Speichern")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function hasMonitorSourceChanges(
  current: GfsMonitorSnapshot,
  preview: GfsMonitorSnapshot,
) {
  return (
    JSON.stringify(stableMonitorSnapshot(current)) !==
    JSON.stringify(stableMonitorSnapshot(preview))
  );
}

function stableMonitorSnapshot(snapshot: GfsMonitorSnapshot) {
  return {
    sourceId: snapshot.sourceId,
    companyId: snapshot.companyId,
    key: snapshot.key,
    name: snapshot.name,
    supplier: snapshot.supplier,
    type: snapshot.type,
    times: snapshot.times,
    capabilities: snapshot.capabilities,
    buyPrice: snapshot.buyPrice,
    image: snapshot.image
      ? {
          fileId: snapshot.image.fileId,
          prefix: snapshot.image.prefix,
        }
      : undefined,
    recommendedVisits: snapshot.recommendedVisits,
    details: snapshot.details,
    pests: snapshot.pests ?? [],
    groups: snapshot.groups ?? [],
  };
}

function CatalogItemImage({
  catalogId,
  item,
}: {
  catalogId: string;
  item: GfsCatalogItem;
}) {
  const { data, isLoading, error } = useGfsCatalogItemShowImageQuery(
    {
      catalogId,
      itemId: item.id,
      fileId: item.image?.fileId ?? "",
      filePrefix: item.image?.prefix ?? "",
    },
    { skip: !item.image?.fileId || !item.image?.prefix },
  );
  const [upload, uploadState] = useGfsCatalogItemImageMutation();
  const [deleteImage, deleteState] = useGfsCatalogItemDeleteImageMutation();

  const uploadFile = (file: File) => {
    void upload({
      catalogId,
      itemId: item.id,
      fileId: v4(),
      file,
    });
  };

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center">
        <Loader className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <ImageUpload
      id={`gfs-catalog-item-image-${item.id}`}
      showImagePreview
      existingImageUrl={data?.objectURL}
      onUpload={uploadFile}
      err={parseRTKQueryError(uploadState.error ?? deleteState.error)}
      disabled={uploadState.isLoading || deleteState.isLoading}
      actions={
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => void deleteImage({ catalogId, itemId: item.id })}
                disabled={
                  uploadState.isLoading || deleteState.isLoading || !item.image
                }
              >
                <Trash2 className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("Bild löschen")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      }
    />
  );
}

function PestAssignment({
  pests,
  selected,
  onChange,
}: {
  pests: Pest[];
  selected: PestSnapshot[];
  onChange: (selected: PestSnapshot[]) => void;
}) {
  const add = (pest: Pest) => {
    if (selected.some((item) => item.sourceId === pest.id)) return;
    onChange([...selected, pestToSnapshot(pest)]);
  };
  const remove = (kind: PestKind, id: string) => {
    onChange(
      selected.filter((item) => !(item.kind === kind && item.sourceId === id)),
    );
  };

  return (
    <div className="grid gap-4">
      <PestSelector
        kind={PestKind.Category}
        pests={pests}
        selected={selected}
        onSelect={add}
      />
      <SelectedPestsTable
        kind={PestKind.Category}
        selected={selected}
        onDeleteSelected={(id) => remove(PestKind.Category, id)}
      />
      <PestSelector
        kind={PestKind.Indicator}
        pests={pests}
        selected={selected}
        onSelect={add}
      />
      <SelectedPestsTable
        kind={PestKind.Indicator}
        selected={selected}
        onDeleteSelected={(id) => remove(PestKind.Indicator, id)}
      />
    </div>
  );
}

function PestSelector({
  kind,
  pests,
  selected,
  onSelect,
}: {
  kind: PestKind;
  pests: Pest[];
  selected: PestSnapshot[];
  onSelect: (pest: Pest) => void;
}) {
  const selectedIds = new Set(
    selected.filter((pest) => pest.kind === kind).map((pest) => pest.sourceId),
  );
  const available = pests
    .filter((pest) => pest.kind === kind && !selectedIds.has(pest.id))
    .sort(comparePests);
  const label =
    kind === PestKind.Category
      ? t("Schädlings-Kategorie hinzufügen")
      : t("Schädlings-Indikator hinzufügen");

  return (
    <Select
      value=""
      disabled={available.length === 0}
      onValueChange={(id) => {
        const selectedPest = available.find((pest) => pest.id === id);
        if (selectedPest) {
          onSelect(selectedPest);
        }
      }}
    >
      <SelectTrigger className="w-[320px]">
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        {available.map((pest) => (
          <SelectItem key={pest.id} value={pest.id}>
            {pest.name}
            {pest.group ? ` (${pest.group.name})` : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SelectedPestsTable({
  kind,
  selected,
  onDeleteSelected,
}: {
  kind: PestKind;
  selected: PestSnapshot[];
  onDeleteSelected: (selectedId: string) => void;
}) {
  const rows = selected
    .filter((pest) => pest.kind === kind)
    .sort((left, right) => left.name.localeCompare(right.name));
  const heading =
    kind === PestKind.Category ? t("Kategorien") : t("Indikatoren");

  return (
    <div className="grid gap-2">
      <div className="text-sm font-medium">{heading}</div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("Name")}</TableHead>
            <TableHead>{t("Gruppe")}</TableHead>
            <TableHead>{t("Abkürzung")}</TableHead>
            <TableHead>{t("Beschreibung")}</TableHead>
            <TableHead className="text-right">{t("Aktion")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((item) => (
            <TableRow key={item.sourceId}>
              <TableCell className="py-1 font-medium">{item.name}</TableCell>
              <TableCell className="py-1">{item.group?.name || "-"}</TableCell>
              <TableCell className="py-1">{item.abbreviation || "-"}</TableCell>
              <TableCell className="py-1">{item.description || "-"}</TableCell>
              <TableCell className="py-1 text-right">
                <Button
                  className="h-fit p-1"
                  size="sm"
                  variant="outline"
                  onClick={() => onDeleteSelected(item.sourceId)}
                >
                  <Trash2 className="size-3" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center">
                -- {t("Noch keine Auswahl")} --
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function GroupSelection({
  groups,
  selected,
  lockedGroupIds,
  lockedGroups,
  onChange,
}: {
  groups: PestGroup[];
  selected: PestGroupSnapshot[];
  lockedGroupIds: Set<string>;
  lockedGroups: PestGroupSnapshot[];
  onChange: (selected: PestGroupSnapshot[]) => void;
}) {
  const selectedIds = new Set(selected.map((group) => group.sourceId));
  const selectableGroups = groups
    .map(groupToSnapshot)
    .filter(
      (group) =>
        !selectedIds.has(group.sourceId) && !lockedGroupIds.has(group.sourceId),
    )
    .sort((left, right) => left.name.localeCompare(right.name));
  const selectedRows = mergeGroups(selected, lockedGroups);

  return (
    <div className="grid gap-2">
      <div className="text-sm font-medium">{t("Schädlingsgruppen")}</div>
      <Select
        value=""
        disabled={selectableGroups.length === 0}
        onValueChange={(id) => {
          const selectedGroup = selectableGroups.find(
            (group) => group.sourceId === id,
          );
          if (selectedGroup) {
            onChange(mergeGroups(selected, [selectedGroup]));
          }
        }}
      >
        <SelectTrigger className="w-[320px]">
          <SelectValue placeholder={t("Schädlingsgruppe hinzufügen")} />
        </SelectTrigger>
        <SelectContent>
          {selectableGroups.map((group) => (
            <SelectItem key={group.sourceId} value={group.sourceId}>
              {group.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("Name")}</TableHead>
            <TableHead>{t("Beschreibung")}</TableHead>
            <TableHead className="text-right">{t("Aktion")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {selectedRows.map((group) => {
            const locked = lockedGroupIds.has(group.sourceId);
            return (
              <TableRow key={group.sourceId}>
                <TableCell className="py-1 font-medium">{group.name}</TableCell>
                <TableCell className="py-1">
                  {group.description || "-"}
                  {locked && (
                    <span className="text-muted-foreground ml-2">
                      {t("durch Schädling gesetzt")}
                    </span>
                  )}
                </TableCell>
                <TableCell className="py-1 text-right">
                  <Button
                    className="h-fit p-1"
                    size="sm"
                    variant="outline"
                    disabled={locked}
                    onClick={() =>
                      onChange(
                        selected.filter(
                          (item) => item.sourceId !== group.sourceId,
                        ),
                      )
                    }
                  >
                    <Trash2 className="size-3" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {selectedRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} className="text-center">
                -- {t("Noch keine Auswahl")} --
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function ConsumablesTable({
  catalogConsumables,
  consumables,
  onChange,
}: {
  catalogConsumables: GfsConsumable[];
  consumables: GfsMonitorConsumable[];
  onChange: (consumables: GfsMonitorConsumable[]) => void;
}) {
  const update = (index: number, item: GfsMonitorConsumable) => {
    const next = [...consumables];
    next[index] = item;
    onChange(next);
  };

  return (
    <div className="grid gap-2">
      <Button
        className="w-fit"
        variant="outline"
        onClick={() =>
          onChange([
            ...consumables,
            {
              consumableId: catalogConsumables[0]?.id ?? "",
              mode: "REFERENCED",
              quantity: 1,
            },
          ])
        }
      >
        <Plus className="size-4" />
        {t("Material hinzufügen")}
      </Button>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("Verbrauchsmaterial")}</TableHead>
            <TableHead>{t("Art")}</TableHead>
            <TableHead>{t("Menge")}</TableHead>
            <TableHead>{t("Preis")}</TableHead>
            <TableHead className="text-right">{t("Aktion")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {consumables.map((item, index) => (
            <TableRow key={index}>
              <TableCell>
                <Select
                  value={item.consumableId}
                  onValueChange={(consumableId) =>
                    update(index, { ...item, consumableId })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {catalogConsumables.map((consumable) => (
                      <SelectItem key={consumable.id} value={consumable.id}>
                        {consumable.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <Select
                  value={item.mode}
                  onValueChange={(mode) =>
                    update(index, {
                      ...item,
                      mode: mode as GfsMonitorConsumable["mode"],
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="REFERENCED">
                      {t("Separat berechnen")}
                    </SelectItem>
                    <SelectItem value="INCLUDED">
                      {t("Im Monitor enthalten")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <FloatNumberInput
                  value={item.quantity}
                  onChange={(quantity) =>
                    update(index, {
                      ...item,
                      quantity,
                    })
                  }
                />
              </TableCell>
              <TableCell>
                <FloatNumberInput
                  value={item.unitPriceOverride ?? 0}
                  onChange={(unitPriceOverride) =>
                    update(index, { ...item, unitPriceOverride })
                  }
                />
              </TableCell>
              <TableCell className="text-right">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() =>
                    onChange(
                      consumables.filter(
                        (_consumable, itemIndex) => itemIndex !== index,
                      ),
                    )
                  }
                >
                  <Trash2 className="size-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {consumables.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center">
                {t("Keine Verbrauchsmaterialien")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
